import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, MapPin } from "@phosphor-icons/react";
import { IconButton } from "@/components/ui-kit/Button";
import { fmtYmdTime } from "@/lib/dateFormat";

/**
 * TripMap — Leaflet-based (OpenStreetMap, free) trip viewer.
 *
 * Fetches /api/operations/{id}/gps-track and renders:
 *   • polyline of all breadcrumb pings
 *   • green pin at the start, red pin at the end (or current position)
 *   • auto-fits the viewport to all coordinates
 *
 * Leaflet is loaded once via CDN <script defer> in index.html. We poll
 * `window.L` for up to ~3s in case the script is still parsing on first open.
 */
export default function TripMap({ tripId, onClose }) {
    const mapRef = useRef(null);
    const containerRef = useRef(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancel = false;
        let pollHandle = null;
        const tryInit = async (attempt = 0) => {
            if (cancel) return;
            if (!window.L) {
                if (attempt > 30) { setError("地図ライブラリの読み込みに失敗しました"); setLoading(false); return; }
                pollHandle = setTimeout(() => tryInit(attempt + 1), 100);
                return;
            }
            try {
                const { data: track } = await api.get(`/operations/${tripId}/gps-track`);
                if (cancel) return;
                setData(track);
                renderMap(track);
            } catch (e) {
                setError(e.response?.data?.detail || "経路の取得に失敗しました");
            } finally {
                setLoading(false);
            }
        };
        tryInit();
        return () => {
            cancel = true;
            if (pollHandle) clearTimeout(pollHandle);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripId]);

    const renderMap = (trip) => {
        const L = window.L;
        if (!L || !containerRef.current) return;
        const points = [];
        const trackPoints = (trip.gps_track || []).map((p) => [p.lat, p.lng]);
        if (trip.gps_start_lat != null && trip.gps_start_lng != null) {
            points.push([trip.gps_start_lat, trip.gps_start_lng]);
        }
        points.push(...trackPoints);
        if (trip.gps_end_lat != null && trip.gps_end_lng != null) {
            points.push([trip.gps_end_lat, trip.gps_end_lng]);
        }
        if (points.length === 0) {
            setError("この運行に位置情報がありません");
            return;
        }
        // Init map
        const center = points[Math.floor(points.length / 2)];
        const map = L.map(containerRef.current, { zoomControl: true }).setView(center, 14);
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "© OpenStreetMap",
            maxZoom: 19,
        }).addTo(map);
        // Polyline of all GPS breadcrumbs
        if (points.length > 1) {
            L.polyline(points, { color: "#22c55e", weight: 4, opacity: 0.85 }).addTo(map);
        }
        // Start marker (green)
        if (trip.gps_start_lat != null) {
            L.circleMarker([trip.gps_start_lat, trip.gps_start_lng], {
                radius: 8, color: "#22c55e", fillColor: "#22c55e", fillOpacity: 1, weight: 2,
            }).addTo(map).bindPopup(`<b>出発</b><br/>${trip.start_time || "-"}`);
        }
        // End marker (red) or current position pulse (if in_progress)
        const isLive = trip.status === "in_progress" || trip.status === "paused";
        const lastPoint = trackPoints.length ? trackPoints[trackPoints.length - 1] : null;
        if (trip.gps_end_lat != null) {
            L.circleMarker([trip.gps_end_lat, trip.gps_end_lng], {
                radius: 8, color: "#ef4444", fillColor: "#ef4444", fillOpacity: 1, weight: 2,
            }).addTo(map).bindPopup(`<b>到着</b><br/>${trip.end_time || "-"}`);
        } else if (lastPoint && isLive) {
            L.circleMarker(lastPoint, {
                radius: 10, color: "#22c55e", fillColor: "#fff", fillOpacity: 1, weight: 3,
                className: "trip-live-marker",
            }).addTo(map).bindPopup(`<b>現在位置</b><br/>${trip.driver_name || "-"}`);
        }
        // Fit bounds
        if (points.length > 1) {
            map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
        }
        mapRef.current = map;
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-3" onClick={onClose} data-testid="trip-map-modal">
            <div className="prc-card p-3 w-full sm:max-w-4xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2">
                    <p className="prc-label-overline mb-0 !text-[var(--prc-green)] inline-flex items-center gap-1">
                        <MapPin size={16} />// 経路マップ
                    </p>
                    <IconButton icon={X} size="sm" title="閉じる" onClick={onClose} data-testid="trip-map-close" />
                </div>
                {data && (
                    <p className="text-xs text-[var(--prc-muted)] font-mono mb-2 truncate">
                        {fmtYmdTime(`${data.operation_date}T${data.start_time || "00:00"}`, `${data.operation_date} ${data.start_time || ""}`)}〜{data.end_time || "進行中"} · {data.vehicle_plate} · {data.driver_name}
                        {data.gps_track?.length > 0 && ` · 経路点 ${data.gps_track.length}`}
                    </p>
                )}
                <div className="flex-1 rounded-sm overflow-hidden border border-[var(--prc-border)] relative">
                    {loading && <div className="absolute inset-0 flex items-center justify-center text-xs text-[var(--prc-muted)] z-10">読込中...</div>}
                    {error && <div className="absolute inset-0 flex items-center justify-center text-xs text-rose-300 z-10">{error}</div>}
                    <div ref={containerRef} className="w-full h-full" data-testid="trip-map-canvas" />
                </div>
            </div>
        </div>
    );
}
